import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import {
  CreditCard,
  Calendar,
  DollarSign,
  Upload,
  CheckCircle,
  AlertCircle,
  Receipt,
  Landmark,
  User,
  FileText,
  Building,
  RefreshCw
} from 'lucide-react';

const ModalMakePaymentForClientPlotsForm = () => {
  const server_app_api_php = window.SERVER_APP_API_PHP;
  const [plotNumberPOP, setPlotNumberPOP] = useState("");
  const [datePOPPlots, setDatePOPPlots] = useState("");
  const [amountPOPPlots, setAmountPOPPlots] = useState("");
  const [POPPlots, setPOPPlots] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [companyID, setCompanyID] = useState("");
  const [businessLocationID, setBusinessLocationID] = useState("");
  const [plotOptions, setPlotOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [selectedPlotDetails, setSelectedPlotDetails] = useState(null);
  const [businessLocations, setBusinessLocations] = useState([]);

  useEffect(() => {
    fetchBusinessLocations();
  }, []);

  useEffect(() => {
    if (businessLocationID) {
      fetchPlotOptions();
    }
  }, [businessLocationID]);

  const fetchBusinessLocations = async () => {
    const formData = new FormData();
    formData.append("apiType", 'getBusinessLocationOnUploadForm');
    formData.append("companyID", 1);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      setBusinessLocations(response.data);
    } catch (error) {
      console.error("Error fetching business locations:", error);
    }
  };

  const fetchPlotOptions = async () => {
    const formData = new FormData();
    formData.append("apiType", 'getPOPPlotsOnUploadForm');
    formData.append("businessLocationID", businessLocationID);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      setPlotOptions(response.data);
    } catch (error) {
      console.error("Error fetching plot options:", error);
    }
  };

  const handleInputChange = (e) => {
    const { id, value, files } = e.target;
    
    if (id === "POPPlots") {
      const file = files[0];
      setPOPPlots(file);
      if (file) {
        // Create preview for image files
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setFilePreview({ type: 'image', url: reader.result });
          };
          reader.readAsDataURL(file);
        } else if (file.type === 'application/pdf') {
          setFilePreview({ type: 'pdf', url: URL.createObjectURL(file) });
        }
      } else {
        setFilePreview(null);
      }
    } else if (id === "plotNumberPOP") {
      setPlotNumberPOP(value);
      const plot = plotOptions.find(p => p.plotsID === value);
      setSelectedPlotDetails(plot);
    } else if (id === "datePOPPlots") {
      setDatePOPPlots(value);
    } else if (id === "amountPOPPlots") {
      setAmountPOPPlots(value);
    } else if (id === "businessLocationID") {
      setBusinessLocationID(value);
      setPlotNumberPOP("");
      setSelectedPlotDetails(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZM', { style: 'currency', currency: 'ZMW' }).format(amount);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitStatus({ type: '', message: '' });

    if (!plotNumberPOP || !datePOPPlots || !amountPOPPlots || !POPPlots) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Please fill all required fields and upload proof of payment.' 
      });
      setIsLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("plotNumberPOP", plotNumberPOP);
    formData.append("datePOPPlots", datePOPPlots);
    formData.append("amountPOPPlots", amountPOPPlots);
    formData.append("POPPlots", POPPlots);
    formData.append("companyID", companyID);
    formData.append("businessLocationID", businessLocationID);
    formData.append("apiType", 'uploadPOPPlots');

    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSubmitStatus({ 
        type: 'success', 
        message: 'Payment uploaded successfully!' 
      });
      
      // Reset form
      setPlotNumberPOP("");
      setDatePOPPlots("");
      setAmountPOPPlots("");
      setPOPPlots(null);
      setFilePreview(null);
      setSelectedPlotDetails(null);
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Error: Please check your internet connection. If this persists, contact support.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = plotNumberPOP && datePOPPlots && amountPOPPlots && POPPlots;

  return (
    <>
      <Header />
      
      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16 pl-64">
        <div className="p-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl">
                <CreditCard className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Plot Payments</h1>
                <p className="text-gray-600 mt-2">Upload proof of payment for plot customers</p>
              </div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Payment Form */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  {/* Form Header */}
                  <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center space-x-3">
                      <Upload className="w-6 h-6 text-blue-500" />
                      <h2 className="text-xl font-bold text-gray-900">New Payment Upload</h2>
                    </div>
                    <p className="text-gray-600 mt-2">Record plot payment with proof of payment document</p>
                  </div>

                  {/* Form Content */}
                  <div className="p-8">
                    {submitStatus.message && (
                      <div className={`mb-6 p-4 rounded-xl ${
                        submitStatus.type === 'success' 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-red-50 border border-red-200'
                      }`}>
                        <div className="flex items-center">
                          {submitStatus.type === 'success' ? (
                            <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                          )}
                          <p className={submitStatus.type === 'success' ? 'text-green-700' : 'text-red-700'}>
                            {submitStatus.message}
                          </p>
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Business Location */}
                      <div>
                        <label htmlFor="businessLocationID" className="block text-sm font-medium text-gray-700 mb-2">
                          <div className="flex items-center">
                            <Building className="w-4 h-4 mr-2 text-gray-400" />
                            Business Location *
                          </div>
                        </label>
                        <select
                          id="businessLocationID"
                          value={businessLocationID}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                          required
                        >
                          <option value="">Select location...</option>
                          {businessLocations.map(location => (
                            <option key={location.businessLocationID} value={location.businessLocationID}>
                              {location.businessLocationName}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Plot Selection */}
                      {businessLocationID && (
                        <div>
                          <label htmlFor="plotNumberPOP" className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center">
                              <Landmark className="w-4 h-4 mr-2 text-gray-400" />
                              Plot Details *
                            </div>
                          </label>
                          <select
                            id="plotNumberPOP"
                            value={plotNumberPOP}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                            required
                          >
                            <option value="">Select plot...</option>
                            {plotOptions.map(option => (
                              <option key={option.plotsID} value={option.plotsID}>
                                Plot {option.plotNumber} - {option.customersNameDB} - {formatCurrency(option.plotPrice)}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Selected Plot Details */}
                      {selectedPlotDetails && (
                        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-gray-600">Plot Number</p>
                              <p className="font-medium text-gray-900">{selectedPlotDetails.plotNumber}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Customer</p>
                              <p className="font-medium text-gray-900">{selectedPlotDetails.customersNameDB}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Plot Price</p>
                              <p className="font-medium text-green-600">{formatCurrency(selectedPlotDetails.plotPrice)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Location</p>
                              <p className="font-medium text-gray-900">{selectedPlotDetails.businessLocationName}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Date and Amount */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="datePOPPlots" className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                              Payment Date *
                            </div>
                          </label>
                          <input
                            type="date"
                            id="datePOPPlots"
                            value={datePOPPlots}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="amountPOPPlots" className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center">
                              <DollarSign className="w-4 h-4 mr-2 text-gray-400" />
                              Amount (ZMW) *
                            </div>
                          </label>
                          <input
                            type="number"
                            id="amountPOPPlots"
                            value={amountPOPPlots}
                            onChange={handleInputChange}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                            required
                          />
                        </div>
                      </div>

                      {/* Proof of Payment Upload */}
                      <div>
                        <label htmlFor="POPPlots" className="block text-sm font-medium text-gray-700 mb-2">
                          <div className="flex items-center">
                            <Receipt className="w-4 h-4 mr-2 text-gray-400" />
                            Proof of Payment *
                          </div>
                        </label>
                        
                        <div className="space-y-4">
                          {filePreview && (
                            <div className="border border-gray-200 rounded-xl p-4">
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-sm font-medium text-gray-700">File Preview</p>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPOPPlots(null);
                                    setFilePreview(null);
                                  }}
                                  className="text-red-500 hover:text-red-700 text-sm"
                                >
                                  Remove
                                </button>
                              </div>
                              {filePreview.type === 'image' && (
                                <img 
                                  src={filePreview.url} 
                                  alt="Preview" 
                                  className="w-full max-h-48 object-contain rounded-lg"
                                />
                              )}
                              {filePreview.type === 'pdf' && (
                                <div className="bg-gray-50 p-4 rounded-lg">
                                  <FileText className="w-12 h-12 text-red-500 mx-auto" />
                                  <p className="text-center mt-2 text-sm text-gray-600">PDF Document</p>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="relative">
                            <input
                              type="file"
                              id="POPPlots"
                              onChange={handleInputChange}
                              accept="image/*,.pdf,.doc,.docx"
                              className="hidden"
                              required
                            />
                            <label
                              htmlFor="POPPlots"
                              className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all duration-300"
                            >
                              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                                <p className="text-sm text-gray-500">
                                  <span className="font-medium text-blue-500">Click to upload</span> or drag and drop
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                  PNG, JPG, PDF, DOC up to 10MB
                                </p>
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Submit Button */}
                      <div className="pt-6">
                        <button
                          type="submit"
                          disabled={!isFormValid || isLoading}
                          className={`w-full py-3.5 px-6 rounded-xl font-medium transition-all duration-300 ${
                            isFormValid && !isLoading
                              ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg hover:shadow-xl'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {isLoading ? (
                            <div className="flex items-center justify-center">
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                              Uploading Payment...
                            </div>
                          ) : (
                            <div className="flex items-center justify-center">
                              <Upload className="w-5 h-5 mr-2" />
                              Upload Payment
                            </div>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Form Footer */}
                  <div className="px-8 py-6 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <AlertCircle className="w-4 h-4 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Supported Formats</p>
                          <p className="text-xs text-gray-500">Images, PDF, Word documents up to 10MB</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">All fields marked with * are required</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Guidelines & Instructions */}
              <div className="space-y-8">
                {/* Payment Guidelines */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-blue-500" />
                    Payment Guidelines
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 mr-3"></div>
                      <span className="text-sm text-gray-700">Ensure payment amount matches the actual transaction</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 mr-3"></div>
                      <span className="text-sm text-gray-700">Upload clear images or PDFs of payment receipts</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 mr-3"></div>
                      <span className="text-sm text-gray-700">Select correct plot and customer for accurate records</span>
                    </li>
                  </ul>
                </div>

                {/* Quick Stats */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <h3 className="font-bold text-gray-900 flex items-center">
                      <CreditCard className="w-5 h-5 mr-2 text-green-500" />
                      Payment Information
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Landmark className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">Available Plots</span>
                        </div>
                        <span className="font-medium text-gray-900">{plotOptions.length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Building className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">Active Locations</span>
                        </div>
                        <span className="font-medium text-gray-900">{businessLocations.length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">Customers</span>
                        </div>
                        <span className="font-medium text-gray-900">
                          {[...new Set(plotOptions.map(p => p.customersNameDB))].length}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Actions */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <h3 className="font-bold text-gray-900 flex items-center">
                      <RefreshCw className="w-5 h-5 mr-2 text-blue-500" />
                      Quick Actions
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="space-y-3">
                      <button 
                        onClick={() => fetchPlotOptions()}
                        className="w-full flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                      >
                        <span className="text-sm font-medium text-blue-700">Refresh Plot List</span>
                        <RefreshCw className="w-4 h-4 text-blue-500" />
                      </button>
                      <button 
                        onClick={() => setBusinessLocationID("")}
                        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <span className="text-sm font-medium text-gray-700">Clear Selection</span>
                        <AlertCircle className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ModalMakePaymentForClientPlotsForm;